import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from '@emotion/styled';
import type { SegmentAggregatedData } from '@trinity/chat/types/segment-aggregated-data';
import type {
  CategoryShare,
  ChartAntiCd38Split,
  ChartArchetypeDistribution,
  ChartDecisionMakingStyle,
  ChartDeescalationOrder,
  ChartEndpointImportance,
  ChartPatientMix,
  ChartRegimenChoiceDrivers,
  ChartRegimenTypeMix,
  ChartTolerabilityRanking,
  ChartTopConstraints,
  ChartTopInformationSources,
} from '@trinity/chat/types/segment-chart-data';

const PALETTE = ['#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F', '#EDC948', '#B07AA1', '#FF9DA7'];

const ARCHETYPE_COLORS: Record<string, string> = {
  Architect: '#4E79A7',
  Traditionalist: '#F28E2B',
  Strategist: '#76B7B2',
  Neophile: '#59A14F',
  Warrior: '#E15759',
  Conservator: '#B07AA1',
  Other: '#BAB0AC',
};

const DECISION_STYLE_COLORS: Record<string, string> = {
  'Strongly Physician Led': '#4E79A7',
  'Shared Decision Making': '#59A14F',
  'Patient Led': '#F28E2B',
  'Depends on Context': '#B07AA1',
};

const ANTI_CD38_COLORS: Record<string, string> = {
  'Elderberry-dominant': '#4E79A7',
  'Mixed Elderberry / non-Elderberry': '#B07AA1',
  'Non-Elderberry': '#F28E2B',
};

function colorFor(label: string, map: Record<string, string>, fallbackIndex: number): string {
  return map[label] ?? PALETTE[fallbackIndex % PALETTE.length];
}

function humanizeLabel(raw: string): string {
  if (!raw) return raw;
  if (!raw.includes('_') && raw !== raw.toLowerCase()) return raw;
  return raw
    .split('_')
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatGeneratedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

interface ChartCardProps {
  title: string;
  subtitle?: string;
  hcpCount: number;
  generatedAt: string;
  isEmpty: boolean;
  children: React.ReactNode;
  height?: number;
}

function ChartCard({
  title,
  subtitle,
  hcpCount,
  generatedAt,
  isEmpty,
  children,
  height = 280,
}: ChartCardProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{title}</CardTitle>
          {subtitle ? <CardSubtitle>{subtitle}</CardSubtitle> : null}
        </div>
        <HeaderMeta>
          <HcpBadge>n = {hcpCount} HCPs</HcpBadge>
          {generatedAt ? <UpdatedLabel>Updated {formatGeneratedAt(generatedAt)}</UpdatedLabel> : null}
        </HeaderMeta>
      </CardHeader>
      <CardBody style={{ height }}>
        {isEmpty ? (
          <EmptyState>No data available for this chart.</EmptyState>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {children as React.ReactElement}
          </ResponsiveContainer>
        )}
      </CardBody>
    </Card>
  );
}

const sliceTooltipStyle = {
  background: '#ffffff',
  border: '1px solid #e5e5e5',
  fontSize: 12,
  borderRadius: 6,
  padding: '6px 10px',
};

function computeYAxisWidth(labels: string[], maxCharsPerLine = 28): number {
  let longestLine = 0;
  for (const label of labels) {
    const lines = wrapLabel(label, maxCharsPerLine);
    for (const line of lines) {
      if (line.length > longestLine) longestLine = line.length;
    }
  }
  const pxPerChar = 6.2;
  const padding = 16;
  return Math.min(180, Math.max(40, Math.ceil(longestLine * pxPerChar) + padding));
}

function wrapLabel(text: string, maxCharsPerLine: number): string[] {
  if (!text) return [''];
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (!current) {
      current = word;
      continue;
    }
    if ((current + ' ' + word).length <= maxCharsPerLine) {
      current = current + ' ' + word;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines.length > 0 ? lines : [text];
}

interface WrappedAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
  axis: 'x' | 'y';
  maxCharsPerLine: number;
  textAnchor?: string;
}

function WrappedAxisTick(props: WrappedAxisTickProps): React.JSX.Element {
  const { x = 0, y = 0, payload, axis, maxCharsPerLine } = props;
  const raw = payload?.value == null ? '' : String(payload.value);
  const lines = wrapLabel(raw, maxCharsPerLine);
  const lineHeight = 13;
  const anchor = axis === 'y' ? 'end' : 'middle';
  const dx = axis === 'y' ? -4 : 0;
  const dy = axis === 'y' ? 4 - ((lines.length - 1) * lineHeight) / 2 : 12;

  return (
    <text x={x} y={y} fill="#323232" fontSize={11} textAnchor={anchor}>
      {lines.map((line, i) => (
        <tspan key={i} x={x + dx} dy={i === 0 ? dy : lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

function sliceTooltipFormatter(
  value: unknown,
  _name: unknown,
  entry: { payload?: CategoryShare }
): [string, string] {
  const payload = entry?.payload;
  if (!payload) return [String(value), ''];
  return [`${payload.count} (${payload.percent}%)`, humanizeLabel(payload.label)];
}

function ArchetypeDistributionBody({
  data,
  ...rest
}: { data: ChartArchetypeDistribution } & Record<string, unknown>): React.JSX.Element {
  const slices = data.slices;
  return (
    <PieChart {...rest}>
      <Pie
        data={slices}
        dataKey="count"
        nameKey="label"
        cx="50%"
        cy="50%"
        innerRadius={34}
        outerRadius={60}
        paddingAngle={1}
      >
        {slices.map((slice, index) => (
          <Cell key={slice.label} fill={colorFor(slice.label, ARCHETYPE_COLORS, index)} />
        ))}
      </Pie>
      <Tooltip contentStyle={sliceTooltipStyle} formatter={sliceTooltipFormatter} />
      <Legend
        layout="horizontal"
        align="center"
        verticalAlign="bottom"
        wrapperStyle={{ fontSize: 11, lineHeight: '22px', paddingTop: 8 }}
        formatter={(value: string, entry) => {
          const payload = entry?.payload as unknown as CategoryShare | undefined;
          if (!payload) return humanizeLabel(value);
          return `${humanizeLabel(payload.label)} (${payload.percent}%)`;
        }}
      />
    </PieChart>
  );
}

export function ArchetypeDistributionChart({
  data,
}: {
  data: ChartArchetypeDistribution;
}): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;

  return (
    <ChartCard
      title="Treatment Archetype Distribution"
      subtitle="Share of HCPs by selected treatment archetype"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
    >
      <ArchetypeDistributionBody data={data} />
    </ChartCard>
  );
}

interface StackedShareRow {
  row: 'All';
  [label: string]: string | number;
}

function StackedShareBar({
  slices,
  colorMap,
  ...rest
}: {
  slices: CategoryShare[];
  colorMap: Record<string, string>;
} & Record<string, unknown>): React.JSX.Element {
  const row = useMemo<StackedShareRow>(() => {
    const out: StackedShareRow = { row: 'All' };
    for (const slice of slices) {
      out[slice.label] = slice.percent;
    }
    return out;
  }, [slices]);

  return (
    <BarChart
      {...rest}
      layout="vertical"
      data={[row]}
      margin={{ top: 0, right: 8, left: 8, bottom: 0 }}
      stackOffset="expand"
      barCategoryGap={0}
    >
      <XAxis type="number" domain={[0, 1]} hide />
      <YAxis type="category" dataKey="row" hide />
      <Tooltip
        contentStyle={sliceTooltipStyle}
        formatter={(value: unknown, name: unknown) => {
          const slice = slices.find((s) => s.label === name);
          if (!slice) return [String(value), humanizeLabel(String(name))];
          return [`${slice.count} (${slice.percent}%)`, humanizeLabel(slice.label)];
        }}
      />
      <Legend
        verticalAlign="bottom"
        wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
        formatter={(value: string) => {
          const slice = slices.find((s) => s.label === value);
          if (!slice) return humanizeLabel(value);
          return `${humanizeLabel(slice.label)} ${slice.percent}%`;
        }}
      />
      {slices.map((slice, index) => (
        <Bar
          key={slice.label}
          dataKey={slice.label}
          stackId="share"
          fill={colorFor(slice.label, colorMap, index)}
          isAnimationActive={false}
          maxBarSize={56}
        >
          <LabelList
            dataKey={slice.label}
            position="center"
            formatter={(v: number) => (v >= 8 ? `${v}%` : '')}
            style={{ fill: '#ffffff', fontSize: 11, fontWeight: 600 }}
          />
        </Bar>
      ))}
    </BarChart>
  );
}

export function RegimenTypeMixChart({ data }: { data: ChartRegimenTypeMix }): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;
  return (
    <ChartCard
      title="Regimen Type Mix"
      subtitle="Primary regimen type per HCP (100% share)"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
      height={200}
    >
      <StackedShareBar slices={data.slices} colorMap={{}} />
    </ChartCard>
  );
}

export function DecisionMakingStyleChart({ data }: { data: ChartDecisionMakingStyle }): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;
  return (
    <ChartCard
      title="Shared Decision-Making Style"
      subtitle="HCP approach to patient conversations (100% share)"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
      height={200}
    >
      <StackedShareBar slices={data.slices} colorMap={DECISION_STYLE_COLORS} />
    </ChartCard>
  );
}

function AntiCd38SplitBody({
  data,
  ...rest
}: { data: ChartAntiCd38Split } & Record<string, unknown>): React.JSX.Element {
  return (
    <BarChart {...rest} data={data.slices} margin={{ top: 16, right: 24, left: 0, bottom: 48 }}>
      <XAxis
        dataKey="label"
        tick={<WrappedAxisTick axis="x" maxCharsPerLine={14} />}
        interval={0}
        height={56}
      />
      <YAxis tick={{ fill: '#626262', fontSize: 11 }} allowDecimals={false} />
      <Tooltip contentStyle={sliceTooltipStyle} formatter={sliceTooltipFormatter} />
      <Bar dataKey="count" isAnimationActive={false}>
        {data.slices.map((slice, index) => (
          <Cell key={slice.label} fill={colorFor(slice.label, ANTI_CD38_COLORS, index)} />
        ))}
        <LabelList
          dataKey="percent"
          position="top"
          formatter={(v: number) => `${v}%`}
          style={{ fill: '#323232', fontSize: 11, fontWeight: 600 }}
        />
      </Bar>
    </BarChart>
  );
}

export function AntiCd38SplitChart({ data }: { data: ChartAntiCd38Split }): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;

  return (
    <ChartCard
      title="Anti-CD38 Usage Split"
      subtitle="HCPs grouped by Elderberry vs. non-Elderberry 1L usage"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
    >
      <AntiCd38SplitBody data={data} />
    </ChartCard>
  );
}

function TopConstraintsBody({
  data,
  ...rest
}: { data: ChartTopConstraints } & Record<string, unknown>): React.JSX.Element {
  const rows = useMemo(
    () =>
      data.slices.map((slice) => ({
        ...slice,
        displayLabel: humanizeLabel(slice.label),
      })),
    [data.slices]
  );

  const yAxisWidth = useMemo(() => computeYAxisWidth(rows.map((r) => r.displayLabel)), [rows]);

  return (
    <BarChart {...rest} layout="vertical" data={rows} margin={{ top: 8, right: 40, left: 0, bottom: 8 }}>
      <XAxis type="number" hide allowDecimals={false} />
      <YAxis
        type="category"
        dataKey="displayLabel"
        tick={<WrappedAxisTick axis="y" maxCharsPerLine={28} />}
        width={yAxisWidth}
        interval={0}
      />
      <Tooltip
        contentStyle={sliceTooltipStyle}
        formatter={(
          value: unknown,
          _name: unknown,
          entry: { payload?: { count: number; percent: number; displayLabel: string } }
        ) => {
          const p = entry?.payload;
          if (!p) return [String(value), ''];
          return [`${p.count} (${p.percent}%)`, p.displayLabel];
        }}
      />
      <Bar dataKey="count" fill={PALETTE[0]} isAnimationActive={false}>
        <LabelList
          dataKey="percent"
          position="right"
          formatter={(v: number) => `${v}%`}
          style={{ fill: '#323232', fontSize: 11, fontWeight: 600 }}
        />
      </Bar>
    </BarChart>
  );
}

export function TopConstraintsChart({ data }: { data: ChartTopConstraints }): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;
  const height = Math.max(220, 40 + data.slices.length * 32);

  return (
    <ChartCard
      title="Top Constraints on Prescribing"
      subtitle="% of HCPs mentioning each driver"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
      height={height}
    >
      <TopConstraintsBody data={data} />
    </ChartCard>
  );
}

// ─── Generic pie body for standard CategoryShare data ──────────────────────

function GenericPieBody({
  slices,
  colorMap,
  ...rest
}: {
  slices: CategoryShare[];
  colorMap: Record<string, string>;
} & Record<string, unknown>): React.JSX.Element {
  return (
    <PieChart {...rest}>
      <Pie
        data={slices}
        dataKey="count"
        nameKey="label"
        cx="50%"
        cy="50%"
        innerRadius={34}
        outerRadius={60}
        paddingAngle={1}
      >
        {slices.map((slice, index) => (
          <Cell key={slice.label} fill={colorFor(slice.label, colorMap, index)} />
        ))}
      </Pie>
      <Tooltip contentStyle={sliceTooltipStyle} formatter={sliceTooltipFormatter} />
      <Legend
        layout="horizontal"
        align="center"
        verticalAlign="bottom"
        wrapperStyle={{ fontSize: 11, lineHeight: '22px', paddingTop: 8 }}
        formatter={(value: string, entry) => {
          const payload = entry?.payload as unknown as CategoryShare | undefined;
          if (!payload) return humanizeLabel(value);
          return `${humanizeLabel(payload.label)} (${payload.percent}%)`;
        }}
      />
    </PieChart>
  );
}

// ─── Generic horizontal bar body (like TopConstraints) ─────────────────────

function GenericHorizontalBarBody({
  slices,
  color,
  ...rest
}: {
  slices: CategoryShare[];
  color: string;
} & Record<string, unknown>): React.JSX.Element {
  const rows = useMemo(
    () =>
      slices.map((slice) => ({
        ...slice,
        displayLabel: humanizeLabel(slice.label),
      })),
    [slices]
  );

  const yAxisWidth = useMemo(() => computeYAxisWidth(rows.map((r) => r.displayLabel)), [rows]);

  return (
    <BarChart {...rest} layout="vertical" data={rows} margin={{ top: 8, right: 40, left: 0, bottom: 8 }}>
      <XAxis type="number" hide allowDecimals={false} />
      <YAxis
        type="category"
        dataKey="displayLabel"
        tick={<WrappedAxisTick axis="y" maxCharsPerLine={28} />}
        width={yAxisWidth}
        interval={0}
      />
      <Tooltip
        contentStyle={sliceTooltipStyle}
        formatter={(
          value: unknown,
          _name: unknown,
          entry: { payload?: { count: number; percent: number; displayLabel: string } }
        ) => {
          const p = entry?.payload;
          if (!p) return [String(value), ''];
          return [`${p.count} (${p.percent}%)`, p.displayLabel];
        }}
      />
      <Bar dataKey="count" fill={color} isAnimationActive={false}>
        <LabelList
          dataKey="percent"
          position="right"
          formatter={(v: number) => `${v}%`}
          style={{ fill: '#323232', fontSize: 11, fontWeight: 600 }}
        />
      </Bar>
    </BarChart>
  );
}

// ─── New chart wrappers ────────────────────────────────────────────────────

const PATIENT_MIX_COLORS: Record<string, string> = {
  'Transplant-Eligible': '#4E79A7',
  'Transplant-Eligible, Not Intended': '#76B7B2',
  'Transplant-Ineligible': '#F28E2B',
};

export function PatientMixChart({ data }: { data: ChartPatientMix }): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;
  return (
    <ChartCard
      title="Patient Mix"
      subtitle="Primary NDMM patient category per HCP"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
    >
      <GenericPieBody slices={data.slices} colorMap={PATIENT_MIX_COLORS} />
    </ChartCard>
  );
}

export function TolerabilityRankingChart({
  data,
}: {
  data: ChartTolerabilityRanking;
}): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;
  const height = Math.max(220, 40 + data.slices.length * 32);
  return (
    <ChartCard
      title="Most Tolerable Drug"
      subtitle="% of HCPs ranking each drug most tolerable"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
      height={height}
    >
      <GenericHorizontalBarBody slices={data.slices} color={PALETTE[0]} />
    </ChartCard>
  );
}

export function EndpointImportanceChart({
  data,
}: {
  data: ChartEndpointImportance;
}): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;
  const height = Math.max(220, 40 + data.slices.length * 32);
  return (
    <ChartCard
      title="Primary 1L Endpoints"
      subtitle="% of HCPs citing each endpoint as primary"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
      height={height}
    >
      <GenericHorizontalBarBody slices={data.slices} color={PALETTE[2]} />
    </ChartCard>
  );
}

export function DeescalationOrderChart({
  data,
}: {
  data: ChartDeescalationOrder;
}): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;
  const height = Math.max(220, 40 + data.slices.length * 32);
  return (
    <ChartCard
      title="Drugs De-escalated"
      subtitle="% of HCPs who drop each component during TIE/TENI de-escalation"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
      height={height}
    >
      <GenericHorizontalBarBody slices={data.slices} color={PALETTE[6]} />
    </ChartCard>
  );
}

export function TopInformationSourcesChart({
  data,
}: {
  data: ChartTopInformationSources;
}): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;
  const height = Math.max(220, 40 + data.slices.length * 32);
  return (
    <ChartCard
      title="Top Information Sources"
      subtitle="% of HCPs citing each source"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
      height={height}
    >
      <GenericHorizontalBarBody slices={data.slices} color={PALETTE[3]} />
    </ChartCard>
  );
}

export function RegimenChoiceDriversChart({
  data,
}: {
  data: ChartRegimenChoiceDrivers;
}): React.JSX.Element {
  const isEmpty = data.hcpCount === 0 || data.slices.length === 0;
  const height = Math.max(220, 40 + data.slices.length * 32);
  return (
    <ChartCard
      title="Regimen Choice Drivers"
      subtitle="% of HCPs citing each driver of 1L regimen selection"
      hcpCount={data.hcpCount}
      generatedAt={data.generatedAt}
      isEmpty={isEmpty}
      height={height}
    >
      <GenericHorizontalBarBody slices={data.slices} color={PALETTE[4]} />
    </ChartCard>
  );
}

// ─── Compact embedded chart for HCP attribute cards ─────────────────────────

const CARD_CHART_HEIGHT = 200;

type EmbeddedCardChartData =
  | { kind: 'archetype'; data: ChartArchetypeDistribution }
  | { kind: 'regimen'; data: ChartRegimenTypeMix }
  | { kind: 'antiCd38'; data: ChartAntiCd38Split }
  | { kind: 'decision'; data: ChartDecisionMakingStyle }
  | { kind: 'constraints'; data: ChartTopConstraints }
  | { kind: 'patientMix'; data: ChartPatientMix }
  | { kind: 'tolerabilityRanking'; data: ChartTolerabilityRanking }
  | { kind: 'endpointImportance'; data: ChartEndpointImportance }
  | { kind: 'deescalationOrder'; data: ChartDeescalationOrder }
  | { kind: 'topInformationSources'; data: ChartTopInformationSources }
  | { kind: 'regimenChoiceDrivers'; data: ChartRegimenChoiceDrivers };

export function EmbeddedCardChart({ chart }: { chart: EmbeddedCardChartData }): React.JSX.Element | null {
  const isEmpty = chart.data.hcpCount === 0 || chart.data.slices.length === 0;
  if (isEmpty) return null;
  if (chart.kind === 'antiCd38' && chart.data.slices.length === 1) return null;

  const horizontalBarKinds = [
    'constraints',
    'tolerabilityRanking',
    'endpointImportance',
    'topInformationSources',
    'regimenChoiceDrivers',
    'deescalationOrder',
  ] as const;
  const isHorizontalBar = (horizontalBarKinds as readonly string[]).includes(chart.kind);

  const height = isHorizontalBar
    ? Math.max(CARD_CHART_HEIGHT, 40 + chart.data.slices.length * 28)
    : chart.kind === 'regimen' || chart.kind === 'decision'
      ? 113
      : chart.kind === 'patientMix' || chart.kind === 'archetype'
        ? 180
        : CARD_CHART_HEIGHT;

  const extraBottomMargin = chart.kind === 'regimen' || chart.kind === 'decision' ? 16 : 0;

  return (
    <EmbeddedChartWrapper style={{ height, marginBottom: extraBottomMargin }}>
      <ResponsiveContainer width="100%" height="100%">
        {chart.kind === 'archetype' ? (
          <ArchetypeDistributionBody data={chart.data} />
        ) : chart.kind === 'antiCd38' ? (
          <AntiCd38SplitBody data={chart.data} />
        ) : chart.kind === 'constraints' ? (
          <TopConstraintsBody data={chart.data} />
        ) : chart.kind === 'regimen' ? (
          <StackedShareBar slices={chart.data.slices} colorMap={{}} />
        ) : chart.kind === 'decision' ? (
          <StackedShareBar slices={chart.data.slices} colorMap={DECISION_STYLE_COLORS} />
        ) : chart.kind === 'patientMix' ? (
          <GenericPieBody slices={chart.data.slices} colorMap={PATIENT_MIX_COLORS} />
        ) : chart.kind === 'deescalationOrder' ? (
          <GenericHorizontalBarBody slices={chart.data.slices} color={PALETTE[6]} />
        ) : chart.kind === 'tolerabilityRanking' ? (
          <GenericHorizontalBarBody slices={chart.data.slices} color={PALETTE[0]} />
        ) : chart.kind === 'endpointImportance' ? (
          <GenericHorizontalBarBody slices={chart.data.slices} color={PALETTE[2]} />
        ) : chart.kind === 'topInformationSources' ? (
          <GenericHorizontalBarBody slices={chart.data.slices} color={PALETTE[3]} />
        ) : (
          <GenericHorizontalBarBody slices={chart.data.slices} color={PALETTE[4]} />
        )}
      </ResponsiveContainer>
    </EmbeddedChartWrapper>
  );
}

export function SegmentChartsPanel({
  aggregated,
}: {
  aggregated: SegmentAggregatedData;
}): React.JSX.Element | null {
  const {
    chartArchetypeDistribution,
    chartRegimenTypeMix,
    chartAntiCd38Split,
    chartDecisionMakingStyle,
    chartTopConstraints,
    chartPatientMix,
    chartTolerabilityRanking,
    chartEndpointImportance,
    chartDeescalationOrder,
    chartTopInformationSources,
    chartRegimenChoiceDrivers,
  } = aggregated;

  const hasAny =
    chartArchetypeDistribution ||
    chartRegimenTypeMix ||
    chartAntiCd38Split ||
    chartDecisionMakingStyle ||
    chartTopConstraints ||
    chartPatientMix ||
    chartTolerabilityRanking ||
    chartEndpointImportance ||
    chartDeescalationOrder ||
    chartTopInformationSources ||
    chartRegimenChoiceDrivers;

  if (!hasAny) return null;

  return (
    <Grid>
      {chartArchetypeDistribution ? <ArchetypeDistributionChart data={chartArchetypeDistribution} /> : null}
      {chartAntiCd38Split ? <AntiCd38SplitChart data={chartAntiCd38Split} /> : null}
      {chartRegimenTypeMix ? <RegimenTypeMixChart data={chartRegimenTypeMix} /> : null}
      {chartDecisionMakingStyle ? <DecisionMakingStyleChart data={chartDecisionMakingStyle} /> : null}
      {chartPatientMix ? <PatientMixChart data={chartPatientMix} /> : null}
      {chartDeescalationOrder ? <DeescalationOrderChart data={chartDeescalationOrder} /> : null}
      {chartTolerabilityRanking ? <TolerabilityRankingChart data={chartTolerabilityRanking} /> : null}
      {chartEndpointImportance ? <EndpointImportanceChart data={chartEndpointImportance} /> : null}
      {chartTopInformationSources ? (
        <TopInformationSourcesChart data={chartTopInformationSources} />
      ) : null}
      {chartRegimenChoiceDrivers ? <RegimenChoiceDriversChart data={chartRegimenChoiceDrivers} /> : null}
      {chartTopConstraints ? (
        <FullWidth>
          <TopConstraintsChart data={chartTopConstraints} />
        </FullWidth>
      ) : null}
    </Grid>
  );
}

const Grid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
  gap: 16,
});

const FullWidth = styled.div({
  gridColumn: '1 / -1',
});

const Card = styled.div({
  background: '#ffffff',
  borderRadius: 12,
  border: '1px solid rgba(0, 0, 0, 0.06)',
  padding: '16px 20px',
  display: 'flex',
  flexDirection: 'column',
});

const CardHeader = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 12,
});

const CardTitle = styled.div({
  fontSize: 16,
  fontWeight: 700,
  color: 'var(--black-l, #323232)',
  fontFamily: 'var(--font-bold, inherit)',
});

const CardSubtitle = styled.div({
  fontSize: 12,
  color: 'rgba(0, 0, 0, 0.54)',
  marginTop: 2,
});

const HeaderMeta = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 4,
});

const HcpBadge = styled.div({
  background: '#f0f4f8',
  borderRadius: 999,
  padding: '2px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: '#323232',
});

const UpdatedLabel = styled.div({
  fontSize: 11,
  color: 'rgba(0, 0, 0, 0.45)',
});

const CardBody = styled.div({
  width: '100%',
});

const EmptyState = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: 'rgba(0, 0, 0, 0.45)',
  fontSize: 13,
  fontStyle: 'italic',
});

const EmbeddedChartWrapper = styled.div({
  width: '100%',
  marginTop: 0,
});
